import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({ title, description, keywords, canonicalUrl, ogType = 'website', schema }: SEOHeadProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = `${title} | 天堂影院`;

    const updateMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      updateMeta('description', description);
    }
    if (keywords) {
      updateMeta('keywords', keywords);
    }

    const currentUrl = canonicalUrl || `${window.location.origin}${location.pathname}`;
    updateMeta('og:url', currentUrl, 'property');
    updateMeta('og:title', `${title} | 天堂影院`, 'property');
    if (description) {
      updateMeta('og:description', description, 'property');
    }
    updateMeta('og:type', ogType, 'property');

    updateMeta('twitter:title', `${title} | 天堂影院`);
    if (description) {
      updateMeta('twitter:description', description);
    }

    // Canonical
    let canonLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonLink) {
      canonLink = document.createElement('link');
      canonLink.rel = 'canonical';
      document.head.appendChild(canonLink);
    }
    canonLink.href = currentUrl;

    // JSON-LD Schema
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
      schemas.forEach((s) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogType, schema, location.pathname]);

  return null;
}